import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  useUserData,
  useUpdateUserData,
} from "components/customHooks/userDataContext";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AddIcCallIcon from "@material-ui/icons/AddIcCall";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import {
  BILLING_PORTAL_ADMIN_GROUP,
  PROVIDER_PORTAL_ADMIN_GROUP,
  PROVIDER_PORTAL_ANSWERING_SERVICE_GROUP,
  PROVIDER_PORTAL_SCRIBE_GROUP,
  USER_MANAGER_ADMIN_GROUP,
  PATIENT_BALANCE_DASHBOARD,
  CLINICAL_CARE_QUALITY_GROUP,
} from "utils/constants";

// eslint-disable-next-line no-undef
const isProduction = process.env.NODE_ENV === "production";

const GoButton = ({ onClick }) => {
  return (
    <button className="go-btn" onClick={onClick}>
      <span className="circle" aria-hidden="true">
        <span className="icon arrow"></span>
      </span>
      <span className="button-text">Go To</span>
    </button>
  );
};

const LinkData = ({ groupName }) => {
  const setUserData = useUpdateUserData();
  const navigate = useNavigate();

  const updateUser = (userType) => {
    setUserData({ userType });
  };

  switch (groupName) {
    case PROVIDER_PORTAL_SCRIBE_GROUP:
      return (
        <div className="group-box">
          <h4>Scribe Dashboard</h4>
          <RecordVoiceOverIcon />
          <div className="descr-cont">
            <p className="card-descr">{"Check doctor's dictations"}</p>
            <GoButton
              onClick={() => {
                // setUserData({ userType: "scribe" });
                updateUser("scribe");
                navigate("/scribe-dashboard");
              }}
            />
          </div>
        </div>
      );
    case PROVIDER_PORTAL_ADMIN_GROUP:
      return (
        <>
          <div className="group-box">
            <h4>Provider Dashboard</h4>
            <LocalHospitalIcon />
            <div className="descr-cont">
              <p className="card-descr">
                Set Appointments, Manage Patients, Reminders, etc.
              </p>
              <GoButton
                onClick={() => {
                  // setUserData({ userType: "provider" });
                  updateUser("provider");
                  navigate("/dashboard");
                }}
              />
            </div>
          </div>
          <div className="group-box">
            <h4>Patient Balance Dashboard</h4>
            <AttachMoneyIcon />
            <div className="descr-cont">
              <p className="card-descr">
                View, Manage Patient Balances and Collection Process{" "}
              </p>
              <GoButton
                onClick={() => {
                  // setUserData({ userType: "provider" });
                  updateUser("patient");
                  navigate("/patient-balance-dashboard");
                }}
              />
            </div>
          </div>
        </>
      );
    case PROVIDER_PORTAL_ANSWERING_SERVICE_GROUP:
      return (
        <div className="group-box">
          <h4>Answering Service</h4>
          <AddIcCallIcon />
          <div className="descr-cont">
            <p className="card-descr">Manage Calls and Messages</p>
            <GoButton
              onClick={() => {
                // setUserData({ userType: "caller" });
                updateUser("caller");
                navigate("/call-dashboard-new");
              }}
            />
          </div>
        </div>
      );
    case CLINICAL_CARE_QUALITY_GROUP:
      return (
        <div className="group-box">
          <h4>Clinical Care</h4>
          <div className="descr-cont">
            <p className="card-descr">Clinical Care Quality Group</p>
            <GoButton
              onClick={() => {
                updateUser("clinical");
                navigate("/clinical-care-dashboard");
              }}
            />
          </div>
        </div>
      );
    case BILLING_PORTAL_ADMIN_GROUP:
      return (
        <div className="group-box">
          <h4>Billing Portal</h4>
          <ReceiptIcon />
          <div className="descr-cont">
            <p className="card-descr">Link to Billing Portal </p>
            <a
              href={
                isProduction
                  ? "https://billing-web.services.orcicare.com/dashboard"
                  : "https://billing-web-test.services.orcicare.com/dashboard"
              }
              target="_blank"
              rel="noreferrer"
            >
              <GoButton onClick={() => {}} />
            </a>
          </div>
        </div>
      );
    case USER_MANAGER_ADMIN_GROUP:
      return (
        <div className="group-box">
          <h4>User Management</h4>
          <PeopleAltOutlinedIcon />
          <div className="descr-cont">
            <p className="card-descr">{"Manage user's logs, tasks, PTOs "}</p>
            <GoButton
              onClick={() => {
                updateUser("admin");
                navigate("/users");
              }}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
};

const MainDashboard = () => {
  const userData = useUserData();
  const navigate = useNavigate();
  const setUserData = useUpdateUserData();

  const userGroups = userData?.groups || [];

  if (!userData) {
    return <Navigate to="/login" replace />;
  }

  // * Navigate when only one group is available
  if (userGroups.length === 1) {
    const groupName = userGroups[0].GroupName;
    switch (groupName) {
      case PROVIDER_PORTAL_SCRIBE_GROUP:
        setUserData({ userType: "scribe" });
        return <Navigate to="/scribe-dashboard" replace />;
      case PROVIDER_PORTAL_ADMIN_GROUP:
        setUserData({ userType: "provider" });
        return <Navigate to="/dashboard" replace />;
      case PROVIDER_PORTAL_ANSWERING_SERVICE_GROUP:
        setUserData({ userType: "caller" });
        return <Navigate to="/call-dashboard-new" replace />;
      case USER_MANAGER_ADMIN_GROUP:
        setUserData({ userType: "admin" });
        return <Navigate to="/users" replace />;
      case PATIENT_BALANCE_DASHBOARD:
        setUserData({ userType: "provider" });
        return <Navigate to="/patient-balance-dashboard" replace />;
      case CLINICAL_CARE_QUALITY_GROUP:
        setUserData({ userType: "clinical" });
        return <Navigate to="/clinical-care-dashboard" replace />;
      case BILLING_PORTAL_ADMIN_GROUP:
        window.location.href = isProduction
          ? "https://billing-web.services.orcicare.com/dashboard"
          : "https://billing-web-test.services.orcicare.com/dashboard";
        break;
      default:
        break;
    }
  }
  // ! REMOVE AND KEEP USER GROUPS
  let tempUserGroups = [...userGroups, { GroupName: USER_MANAGER_ADMIN_GROUP }];
  return (
    <div className="main-dashboard">
      <main>
        <h1>Welcome To Orcicare!</h1>
        <p className="description">{"Let's Simplify Healthcare"}</p>
        {!userGroups || !(userGroups?.length > 1) ? (
          <div className="no-group">
            <p>
              Your account is not associated with any of available services.
            </p>
            <p>Please contact support for assistance.</p>
            <button onClick={() => navigate("/login")}>Logout</button>
          </div>
        ) : (
          <div className="group-cont">
            {tempUserGroups.map((group) => (
              <LinkData groupName={group.GroupName} key={group.GroupName} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MainDashboard;
